import { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../lib/logger';
import Button from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to our logging service
    logger.error('Component error:', { 
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack 
    });
  }

  handleReset = (): void => {
    const { onReset } = this.props;
    
    this.setState({
      hasError: false,
      error: null
    });
    
    if (onReset) {
      onReset();
    }
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // You can render any custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6 bg-red-50 rounded-lg border border-red-100">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          
          <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          
          {error && (
            <p className="text-sm text-gray-600 mb-4 max-w-md text-center">
              {error.message || 'An unexpected error occurred'}
            </p>
          )}
          
          <Button onClick={this.handleReset} variant="primary">
            Try again
          </Button>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary; 